/**
 * Font registry: built-in catalog + user-added fonts (CDN URLs or uploaded files).
 *
 * Built-in fonts include the ones INTA uses (Pretendard, Outfit) plus a strong
 * Korean + Latin starter pack so the editor is useful out of the box. None of
 * these are hosted on our server — they all load from public CDNs (Google
 * Fonts, jsDelivr). User-added fonts persist in IndexedDB so big base64
 * uploaded fonts don't run into localStorage's ~5MB cap.
 */

import { dbGet, dbSet } from '../db/idb';

const STORAGE_KEY = 'promo-studio:user-fonts'; // legacy localStorage key (one-shot migration)
const IDB_KEY = 'fonts:user';
const LOADED = new Set<string>();
const LISTENERS = new Set<() => void>();

export type FontSource = 'builtin' | 'google' | 'noonnu' | 'upload' | 'cdn';

export interface FontEntry {
  family: string;
  source: FontSource;
  /** Stylesheet URL — Google CSS API or jsDelivr CSS file. */
  cssUrl?: string;
  /** Direct font file URL (.ttf/.otf/.woff2). */
  fontUrl?: string;
  /** data: URL for uploaded files. */
  dataUrl?: string;
  weight?: number;
  category?: 'sans' | 'serif' | 'mono' | 'display' | 'handwriting';
  /** Languages: 'korean' | 'latin' | etc. */
  scripts?: string[];
  /** Short description shown under preview. */
  hint?: string;
}

export const BUILTIN_FONTS: FontEntry[] = [
  // Korean
  {
    family: 'Pretendard',
    source: 'builtin',
    cssUrl: 'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.css',
    category: 'sans',
    scripts: ['korean', 'latin'],
    hint: '인타 기본 · 한글 산세리프',
  },
  {
    family: 'Noto Sans KR',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;700;900&display=swap',
    category: 'sans',
    scripts: ['korean'],
    hint: '구글 한글 표준',
  },
  {
    family: 'Noto Serif KR',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@300;400;500;700;900&display=swap',
    category: 'serif',
    scripts: ['korean'],
    hint: '한글 명조',
  },
  {
    family: 'Black Han Sans',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Black+Han+Sans&display=swap',
    category: 'display',
    scripts: ['korean'],
    hint: '임팩트 헤드라인',
  },
  {
    family: 'Jua',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Jua&display=swap',
    category: 'display',
    scripts: ['korean'],
    hint: '둥글둥글 친근',
  },
  {
    family: 'Do Hyeon',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Do+Hyeon&display=swap',
    category: 'display',
    scripts: ['korean'],
    hint: '굵은 헤드라인',
  },
  {
    family: 'Gowun Dodum',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Gowun+Dodum&display=swap',
    category: 'sans',
    scripts: ['korean'],
    hint: '부드러운 본문',
  },
  {
    family: 'Gowun Batang',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Gowun+Batang:wght@400;700&display=swap',
    category: 'serif',
    scripts: ['korean'],
    hint: '부드러운 명조',
  },
  {
    family: 'Cute Font',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Cute+Font&display=swap',
    category: 'handwriting',
    scripts: ['korean'],
    hint: '귀여운 손글씨',
  },
  {
    family: 'Single Day',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Single+Day&display=swap',
    category: 'handwriting',
    scripts: ['korean'],
    hint: '가벼운 손글씨',
  },
  {
    family: 'Stylish',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Stylish&display=swap',
    category: 'sans',
    scripts: ['korean'],
    hint: '깔끔 헤드라인',
  },
  {
    family: 'Sunflower',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Sunflower:wght@300;500;700&display=swap',
    category: 'sans',
    scripts: ['korean'],
    hint: '경쾌한 산세리프',
  },
  {
    family: 'Hi Melody',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Hi+Melody&display=swap',
    category: 'handwriting',
    scripts: ['korean'],
    hint: '아동 캐주얼',
  },
  {
    family: 'Diphylleia',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Diphylleia&display=swap',
    category: 'serif',
    scripts: ['korean'],
    hint: '엘레강스 명조',
  },
  // Latin
  {
    family: 'Outfit',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;700;900&display=swap',
    category: 'sans',
    scripts: ['latin'],
    hint: '인타 보조 · 지오메트릭 산세리프',
  },
  {
    family: 'Inter',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;900&display=swap',
    category: 'sans',
    scripts: ['latin'],
  },
  {
    family: 'Bebas Neue',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap',
    category: 'display',
    scripts: ['latin'],
    hint: 'Tall poster headline',
  },
  {
    family: 'Anton',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Anton&display=swap',
    category: 'display',
    scripts: ['latin'],
    hint: 'Heavy condensed display',
  },
  {
    family: 'Archivo Black',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Archivo+Black&display=swap',
    category: 'display',
    scripts: ['latin'],
  },
  {
    family: 'Montserrat',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;700;900&display=swap',
    category: 'sans',
    scripts: ['latin'],
  },
  {
    family: 'Poppins',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;700;900&display=swap',
    category: 'sans',
    scripts: ['latin'],
  },
  {
    family: 'Oswald',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;600;700&display=swap',
    category: 'sans',
    scripts: ['latin'],
  },
  {
    family: 'JetBrains Mono',
    source: 'google',
    cssUrl: 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&display=swap',
    category: 'mono',
    scripts: ['latin'],
  },
];

export interface FontStore {
  builtin: FontEntry[];
  user: FontEntry[];
}

function loadUserFontsLegacy(): FontEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as FontEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveUserFonts(list: FontEntry[]): void {
  // Fire-and-forget; IDB is async but the in-memory list is already updated.
  void dbSet(IDB_KEY, list).catch((err) => console.warn('Font save failed', err));
}

let userFonts: FontEntry[] = [];

/** Loads user fonts from IDB. Migrates from legacy localStorage if present. */
export async function initFonts(): Promise<void> {
  try {
    const fromIdb = await dbGet<FontEntry[]>(IDB_KEY);
    if (Array.isArray(fromIdb)) {
      userFonts = fromIdb;
    } else {
      const legacy = loadUserFontsLegacy();
      if (legacy.length) {
        userFonts = legacy;
        await dbSet(IDB_KEY, legacy);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    notify();
  } catch (err) {
    console.warn('Font init failed', err);
  }
}

export function getAllFonts(): FontStore {
  return { builtin: BUILTIN_FONTS, user: [...userFonts] };
}

export function addUserFont(entry: FontEntry): void {
  const existing = userFonts.findIndex((f) => f.family === entry.family);
  if (existing >= 0) userFonts[existing] = entry;
  else userFonts = [...userFonts, entry];
  saveUserFonts(userFonts);
  ensureLoaded(entry);
  notify();
}

export function removeUserFont(family: string): void {
  userFonts = userFonts.filter((f) => f.family !== family);
  saveUserFonts(userFonts);
  notify();
}

export function subscribeFonts(fn: () => void): () => void {
  LISTENERS.add(fn);
  fn();
  return () => LISTENERS.delete(fn);
}

function notify(): void {
  for (const fn of LISTENERS) fn();
}

/** Add a <link>/<style> tag for a font if it isn't loaded yet. Idempotent. */
export function ensureLoaded(entry: FontEntry): void {
  if (LOADED.has(entry.family)) return;
  LOADED.add(entry.family);

  if (entry.cssUrl) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = entry.cssUrl;
    link.crossOrigin = 'anonymous';
    link.dataset.font = entry.family;
    document.head.appendChild(link);
    return;
  }

  if (entry.dataUrl || entry.fontUrl) {
    const url = entry.dataUrl ?? entry.fontUrl!;
    const style = document.createElement('style');
    const weight = entry.weight ?? 400;
    style.textContent = `@font-face{font-family:"${entry.family.replace(/"/g, '\\"')}";src:url("${url}");font-weight:${weight};font-display:swap;}`;
    style.dataset.font = entry.family;
    document.head.appendChild(style);
  }
}

export function ensureAllLoaded(): void {
  for (const entry of BUILTIN_FONTS) ensureLoaded(entry);
  for (const entry of userFonts) ensureLoaded(entry);
}

/** Find a font entry by family name, searching both builtin and user lists. */
export function findFont(family: string): FontEntry | null {
  return BUILTIN_FONTS.find((f) => f.family === family) ?? userFonts.find((f) => f.family === family) ?? null;
}
